import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import {
  normalizeMenu,
  applyEditItem,
  applyAddItem,
  applyDeleteItem,
  applyReorderItem,
  applyEditCategory,
  applyEditCategoryAvailableDuring,
  applyEditItemAvailableDuring,
  applyAddCategory,
  applyDeleteCategory,
  MAP_MODE_EXISTING,
  MAP_MODE_NEW,
  makeDefaultCategoryMapping,
  resolveTargetCategoryName,
  isDuplicateItem,
  defaultItemIncluded,
  itemDecisionKey,
  normalizeTitle,
  transformMenuToBulkProducts,
} from './previewEditReducer';
import { bulkCreateProducts } from '../services/menuImportClient';

const API_BASE_URL =
  process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

/**
 * Normalize the extracted embedded images (Phase 2, Req 10.2) into a deduped
 * list of `{ url, label }` for the per-item association control. Reads the
 * `embeddedImages` prop first, then falls back to the extraction result the
 * upload page forwards on the `menu` object (the backend field name is
 * finalized in task 13.1, so several likely shapes are accepted defensively).
 * Each entry may be a plain URL string or an object carrying a
 * url/imageUrl/ImageURL/src field. Absent/garbage input yields `[]`, so Phase 1
 * documents (which carry no images) simply get no association control (Req 11.2).
 */
function normalizeEmbeddedImages(propImages, menu) {
  const candidates = [
    propImages,
    menu && menu.embeddedImages,
    menu && menu.images,
    menu && menu.embedded_images,
    menu && menu.Embedded_Images,
  ];
  const raw = candidates.find((c) => Array.isArray(c)) || [];

  const seen = new Set();
  const out = [];
  raw.forEach((entry) => {
    let url = '';
    if (typeof entry === 'string') {
      url = entry.trim();
    } else if (entry && typeof entry === 'object') {
      const found = entry.url || entry.imageUrl || entry.ImageURL || entry.src || '';
      url = typeof found === 'string' ? found.trim() : '';
    }
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push({ url, label: `Photo ${out.length + 1}` });
  });
  return out;
}

/**
 * Read the Flat_Scan flag (Req 10.4) from the `flatScan` prop or, defensively,
 * from the forwarded `menu` object under a few likely field names. Defaults to
 * `false` when absent (Phase 1 documents carry no flag).
 */
function readFlatScanFlag(propFlatScan, menu) {
  if (typeof propFlatScan === 'boolean') return propFlatScan;
  if (menu && typeof menu === 'object') {
    if (typeof menu.flatScan === 'boolean') return menu.flatScan;
    if (typeof menu.isFlatScan === 'boolean') return menu.isFlatScan;
    if (typeof menu.flat_scan === 'boolean') return menu.flat_scan;
    if (typeof menu.Flat_Scan === 'boolean') return menu.Flat_Scan;
  }
  return false;
}

/* ---------------------------------------------------------------------------
 * AI-suggested modifier groups (Phase 3, Feature: ai-menu-image-import, Req 12–13)
 *
 * The helpers below are PURE (no React, no state, no network) so they can be
 * reasoned about (and, per task 18.1/18.2, property-tested) independently. They
 * read AI-proposed Modifier_Groups defensively off each extracted item (absent
 * in Phase 1/2 → treated as no groups), normalize them into an editable working
 * shape, validate an accepted group's min/max + selection rule (Req 13.1, 13.2),
 * and map an accepted group onto the existing catalog storage contract consumed
 * by `_normalize_modifier_groups_for_storage` on POST /admin/products/bulk —
 * either INLINE (`optionsSource:'INLINE'` with `options` carrying
 * `priceDeltaCents` + `defaultSelected`) or TEMPLATE (`templateId`, no inline
 * options) (Req 12.5). None of this persists anything; nothing is saved unless
 * the Admin explicitly accepts a group and then confirms (Req 12.4, 14.3).
 * ------------------------------------------------------------------------- */

/** Canonical group types offered in the editor (Req 12.1). */
const MODIFIER_GROUP_TYPES = ['Included-removals', 'Extras-add-ons', 'Size'];

// Coerce any value to a trimmed-safe string ('' for null/undefined). Extracted
// text is inert data, so we only string-coerce it, never interpret it (Req 4.4).
function asString(value) {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value : String(value);
}

// Coerce a value to an integer, falling back to `fallback` when not parseable.
function toIntOr(value, fallback) {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  const str = asString(value).trim();
  if (!str) return fallback;
  const n = Number.parseFloat(str.replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

/**
 * Parse a human price (e.g. "$2.00", "2", 2) into integer cents. Returns
 * `{ cents, detected }` where `detected` is false when no price could be read,
 * so the caller can flag the option for the Admin to confirm (Req 13.3 mirror).
 */
function parsePriceToCents(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { cents: Math.round(value * 100), detected: true };
  }
  const str = asString(value).trim();
  if (!str) return { cents: 0, detected: false };
  const cleaned = str.replace(/[^0-9.\-]/g, '');
  if (!cleaned || cleaned === '.' || cleaned === '-') return { cents: 0, detected: false };
  const dollars = Number.parseFloat(cleaned);
  if (!Number.isFinite(dollars)) return { cents: 0, detected: false };
  return { cents: Math.round(dollars * 100), detected: true };
}

/** Normalize an arbitrary selection value to the canonical 'Single'/'Multiple'. */
function normalizeSelection(sel) {
  const s = asString(sel).trim().toLowerCase();
  if (s === 'multiple' || s === 'multi' || s === 'many') return 'Multiple';
  return 'Single';
}

/** Normalize an arbitrary group-type value to one of MODIFIER_GROUP_TYPES. */
function normalizeGroupType(type) {
  const s = asString(type).trim().toLowerCase();
  if (s.includes('size')) return 'Size';
  if (s.includes('extra') || s.includes('add')) return 'Extras-add-ons';
  if (s.includes('includ') || s.includes('remov')) return 'Included-removals';
  return 'Extras-add-ons';
}

/**
 * Read AI-proposed Modifier_Groups off an extracted item defensively. The
 * Phase 3 extraction field name is finalized backend-side, so several likely
 * shapes are accepted; Phase 1/2 items carry none → `[]` (no groups rendered).
 * Only object entries are kept.
 */
function readProposedGroups(item) {
  if (!item || typeof item !== 'object') return [];
  const candidates = [
    item.modifierGroups,
    item.ModifierGroups,
    item.modifier_groups,
    item.Modifier_Groups,
    item.modifiers,
  ];
  const raw = candidates.find((c) => Array.isArray(c)) || [];
  return raw.filter((g) => g && typeof g === 'object');
}

/** Format an integer number of cents as an editable dollar string ("150" -> "1.50"). */
function centsToDollarString(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (Math.max(0, Math.round(n)) / 100).toFixed(2);
}

/**
 * Normalize a single proposed option into the editable working shape.
 *
 * The upcharge is held as a DOLLAR string (`priceDelta`, e.g. "1.50") so the
 * Admin edits it in the same units as item prices — NOT cents. It is converted
 * back to the storage contract's integer `priceDeltaCents` only in
 * `mapGroupToStorage` (Req 12.5). Gemini may hand us either a dollar `price`
 * or an integer `priceDeltaCents`; both are coerced to the dollar string here.
 */
function normalizeProposedOption(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  let cents = 0;
  let detected = false;
  if (o.priceDeltaCents !== null && o.priceDeltaCents !== undefined && o.priceDeltaCents !== '') {
    const n = Number(o.priceDeltaCents);
    if (Number.isFinite(n)) {
      cents = Math.round(n);
      detected = true;
    }
  } else {
    const parsed = parsePriceToCents(o.price !== undefined ? o.price : o.Price);
    cents = parsed.cents;
    detected = parsed.detected;
  }
  if (cents < 0) cents = 0;
  const def = o.defaultSelected !== undefined ? o.defaultSelected : o.default;
  return {
    name: asString(o.name || o.Name),
    // Dollar string for editing; '' when no price was detected so the field
    // shows its "0.00" placeholder and is flagged for the Admin to confirm.
    priceDelta: detected ? centsToDollarString(cents) : '',
    defaultSelected: Boolean(def),
    // An option with no detectable price defaults to 0 and is flagged for the
    // Admin to confirm (Req 12.5 / mirror 13.3).
    priceMissing: !detected,
  };
}

/**
 * Normalize a single proposed Modifier_Group into the editable working shape,
 * defaulting a Single-selection group's min/max toward 1 (Req 13.1 semantics).
 */
function normalizeProposedGroup(raw) {
  const g = raw && typeof raw === 'object' ? raw : {};
  const selection = normalizeSelection(g.selection || g.selectionMode || g.selectionRule);
  const min = toIntOr(g.min !== undefined ? g.min : g.minSelections, selection === 'Single' ? 1 : 0);
  const maxDefault = selection === 'Single' ? 1 : Math.max(min, 1);
  const max = toIntOr(g.max !== undefined ? g.max : g.maxSelections, maxDefault);
  const source = asString(g.optionsSource || g.OptionsSource || g.options_source).toUpperCase();
  const templateId = asString(g.templateId || g.TemplateID || g.template_id);
  const rawOptions = Array.isArray(g.options)
    ? g.options
    : Array.isArray(g.Options)
    ? g.Options
    : [];
  return {
    name: asString(g.name || g.Name || g.groupName),
    groupType: normalizeGroupType(g.groupType || g.GroupType || g.type),
    selection,
    min,
    max,
    optionsSource: source === 'TEMPLATE' && templateId ? 'TEMPLATE' : 'INLINE',
    templateId,
    options: rawOptions.map((o) => normalizeProposedOption(o)),
  };
}

/**
 * Validate a working Modifier_Group before it may be accepted for import
 * (Req 13.1, 13.2): min ≤ max, both non-negative integers, and the selection
 * rule is consistent (a Single-choice group allows exactly one selection, so
 * max must be 1 and min 0 or 1). Returns `{ ok, error }`; the error string is
 * surfaced in the announced (aria-live/role=alert) region on an accept attempt.
 */
function validateModifierGroup(group) {
  const g = group && typeof group === 'object' ? group : {};
  const min = Number(g.min);
  const max = Number(g.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { ok: false, error: 'Minimum and maximum must be numbers.' };
  }
  if (!Number.isInteger(min) || !Number.isInteger(max)) {
    return { ok: false, error: 'Minimum and maximum must be whole numbers.' };
  }
  if (min < 0 || max < 0) {
    return { ok: false, error: 'Minimum and maximum must be zero or greater.' };
  }
  if (min > max) {
    return { ok: false, error: 'Minimum must be less than or equal to maximum.' };
  }
  if (normalizeSelection(g.selection) === 'Single' && (max !== 1 || min > 1)) {
    return {
      ok: false,
      error: 'Single-choice groups must allow exactly one selection (min 0 or 1, max 1).',
    };
  }
  return { ok: true, error: '' };
}

/**
 * Map an accepted working Modifier_Group onto the existing catalog storage
 * contract (Req 12.5). A TEMPLATE group carries `templateId` and no inline
 * options; an INLINE group carries its own `options` with `priceDeltaCents`
 * (clamped ≥ 0) and `defaultSelected`. Blank-named options are dropped.
 */
function mapGroupToStorage(group) {
  const g = group && typeof group === 'object' ? group : {};
  const selection = normalizeSelection(g.selection);
  const min = toIntOr(g.min, 0);
  const max = toIntOr(g.max, selection === 'Single' ? 1 : Math.max(min, 1));
  const base = {
    name: asString(g.name),
    groupType: normalizeGroupType(g.groupType),
    selection,
    min,
    max,
  };
  if (asString(g.optionsSource).toUpperCase() === 'TEMPLATE' && asString(g.templateId)) {
    return { ...base, optionsSource: 'TEMPLATE', templateId: asString(g.templateId) };
  }
  const options = Array.isArray(g.options) ? g.options : [];
  return {
    ...base,
    optionsSource: 'INLINE',
    options: options
      .filter((o) => asString(o && o.name).trim() !== '')
      .map((o) => {
        // The Admin edits the upcharge in DOLLARS (`priceDelta`); convert to the
        // storage contract's integer cents here. Fall back to a legacy
        // `priceDeltaCents` field if present (defensive).
        let cents;
        if (o && o.priceDelta !== undefined) {
          cents = parsePriceToCents(o.priceDelta).cents;
        } else {
          cents = toIntOr(o && o.priceDeltaCents, 0);
        }
        if (!Number.isFinite(cents) || cents < 0) cents = 0;
        return {
          name: asString(o.name),
          priceDeltaCents: cents,
          defaultSelected: Boolean(o.defaultSelected),
        };
      }),
  };
}

/** Stable key for an item's proposed group, keyed by item + group index so a
 *  decision/edit survives row reordering (mirrors itemDecisionKey usage). */
function modifierGroupKey(targetCategoryName, itemTitle, groupIdx) {
  return `${itemDecisionKey(targetCategoryName, itemTitle)}\u0000g${groupIdx}`;
}

/**
 * PreviewEditScreen
 *
 * Editable preview of an Extracted_Menu for the AI Menu Import feature
 * (Feature: ai-menu-image-import, Req 5). It receives the extracted menu as
 * the `menu` prop and holds ALL working state locally in React state. It
 * issues ZERO catalog writes and makes NO network calls: every edit is a pure
 * state transition (see ./previewEditReducer). Category mapping, duplicate
 * flagging, the reviewed→bulk transform, and the confirm/bulk-create network
 * behavior are implemented by later tasks (9.1, 9.3, 9.5). The "Confirm
 * import" button below is a wired-in-later placeholder (task 9.5) and performs
 * no persistence here (Req 5.7, 14.1).
 *
 * Phase 2 (task 14) adds OPTIONAL per-item image association (Req 10.2, 10.3,
 * 11.1, 11.2). The backend extraction (task 13.1) may return truly embedded
 * image URLs plus a flat-scan flag; those are read from props (or, defensively,
 * from the `menu` object MenuImportPage forwards) and their absence is handled
 * gracefully (Phase 1 documents carry neither). When embedded images exist the
 * admin may associate one per item; the associated `imageUrl` is attached to a
 * product body ONLY when an image was chosen, and association NEVER blocks item
 * creation. For a Flat_Scan with no separable images we show an informational
 * note that photos can be added manually and offer no association control.
 *
 * Props:
 *   - menu:           the Extracted_Menu ({ categories: [ { title, items:[...] } ] })
 *   - isManualEntry:  true when the admin chose manual entry (empty menu)
 *   - onExit:         optional callback to leave the preview / start over
 *   - embeddedImages: optional array of extracted Embedded_Image URLs (Phase 2,
 *                     Req 10.2); each entry may be a URL string or an object
 *                     carrying a url/imageUrl/src field. Absent in Phase 1.
 *   - flatScan:       optional boolean flag; true when the document was a
 *                     Flat_Scan with no separable embedded images (Req 10.4).
 */
export default function PreviewEditScreen({
  menu,
  isManualEntry = false,
  onExit,
  embeddedImages,
  flatScan,
}) {
  // Seed local working state from the incoming menu, normalized to a
  // well-formed shape. This is the single source of truth while editing —
  // nothing here touches the catalog until confirm (task 9.5).
  const [workingMenu, setWorkingMenu] = useState(() => normalizeMenu(menu));

  // Per-category mapping intent, aligned by index with workingMenu.categories
  // (Req 6.1). Each entry is { mode, existingName, newName }. Captured locally
  // only — no category is created server-side here (that happens on confirm,
  // task 9.5).
  const [categoryMappings, setCategoryMappings] = useState(() =>
    normalizeMenu(menu).categories.map((cat) => makeDefaultCategoryMapping(cat))
  );

  // Existing catalog categories/products, fetched read-only to power the
  // mapping dropdown (Req 6.1) and duplicate flagging (Req 6.2). Reads only —
  // this screen performs no catalog writes.
  const [existingCategories, setExistingCategories] = useState([]);
  const [existingProducts, setExistingProducts] = useState([]);
  const [catalogError, setCatalogError] = useState('');

  // Explicit Admin include/exclude decisions for flagged duplicates, keyed by
  // stable content key (survives row reordering). Absent key => use the
  // default (a flagged duplicate defaults to excluded — Req 6.4).
  const [includeOverrides, setIncludeOverrides] = useState({});

  // Optional per-item image association (Phase 2, Req 10.2, 10.3). Keyed by the
  // same stable content key used for include/exclude decisions so the choice
  // survives row reordering; an absent/empty entry means "no photo" and no
  // imageUrl is ever attached (Req 11.1). Association is purely local intent —
  // nothing is persisted until the Admin confirms.
  const [imageAssociations, setImageAssociations] = useState({});

  // AI-suggested modifier groups (Phase 3, Req 12–13).
  const [groupStates, setGroupStates] = useState({});
  const [modifierError, setModifierError] = useState('');

  // Meal period / service window names — loaded from /admin/general when
  // serviceWindows are configured. Empty = feature not enabled for this tenant.
  const [serviceWindowNames, setServiceWindowNames] = useState([]);

  // Confirm/bulk-create state (task 9.5).
  const [isConfirming, setIsConfirming] = useState(false);
  const [summary, setSummary] = useState(null);
  const [confirmError, setConfirmError] = useState('');

  const categories = workingMenu.categories;

  const totalItems = useMemo(
    () => categories.reduce((sum, cat) => sum + cat.items.length, 0),
    [categories]
  );

  // Embedded images available for per-item association (Phase 2, Req 10.2).
  // Read from the `embeddedImages` prop first, then fall back to the extraction
  // result MenuImportPage forwards on the `menu` object (the backend field name
  // is finalized in task 13.1). Absent in Phase 1 → an empty list, so no
  // association control is offered (Req 11.2). Deduped and given stable labels.
  const availableImages = useMemo(
    () => normalizeEmbeddedImages(embeddedImages, menu),
    [embeddedImages, menu]
  );
  const hasEmbeddedImages = availableImages.length > 0;

  // Flat_Scan flag (Req 10.4): true when the document was a single raster scan
  // with no separable embedded image objects. Read from the `flatScan` prop or
  // the forwarded `menu` object; false/absent in Phase 1. We only surface the
  // "add photos manually" note when the document is a flat scan AND carries no
  // embedded images to associate (Req 10.4, 11.2).
  const isFlatScan = useMemo(() => readFlatScanFlag(flatScan, menu), [flatScan, menu]);
  const showFlatScanNote = isFlatScan && !hasEmbeddedImages;

  // Number of table columns, so the empty-state row spans correctly.
  // Columns: Item, Description, Price, [Meal Period], [Photo], Import, Actions
  const itemColumnCount = 5 + (serviceWindowNames.length > 0 ? 1 : 0) + (hasEmbeddedImages ? 1 : 0);

  // Fetch existing categories + products + service window config once, read-only.
  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      if (!API_BASE_URL) return;
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const headers = { Authorization: `Bearer ${token}` };

        const [catRes, prodRes, settingsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/categories`, { headers }),
          fetch(`${API_BASE_URL}/admin/products`, { headers }),
          fetch(`${API_BASE_URL}/admin/general`, { headers }),
        ]);

        if (!catRes.ok || !prodRes.ok) {
          throw new Error('Failed to load catalog');
        }
        const catData = await catRes.json().catch(() => ({}));
        const prodData = await prodRes.json().catch(() => ({}));
        const settingsData = settingsRes.ok ? await settingsRes.json().catch(() => ({})) : {};
        if (cancelled) return;
        setExistingCategories(Array.isArray(catData.categories) ? catData.categories : []);
        setExistingProducts(Array.isArray(prodData.products) ? prodData.products : []);
        setCatalogError('');

        // Extract service window names. Only populate when serviceWindows is
        // enabled — if disabled or unconfigured, the meal period UI is hidden.
        const settings = settingsData?.settings ?? settingsData ?? {};
        const swConfig = settings?.serviceWindows ?? {};
        if (swConfig?.enabled && Array.isArray(swConfig?.windows)) {
          const names = [];
          for (const w of swConfig.windows) {
            const name = String(w?.name || '').trim();
            if (name && !names.some(n => n.toLowerCase() === name.toLowerCase())) {
              names.push(name);
            }
          }
          setServiceWindowNames(names);
        }
      } catch (err) {
        if (cancelled) return;
        setCatalogError(
          'Could not load existing categories/products. You can still map to new categories.'
        );
      }
    };
    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  // Names of existing catalog categories for the mapping dropdown (Req 6.1).
  const existingCategoryNames = useMemo(() => {
    const names = existingCategories
      .map((c) => (c && c.ProductName != null ? String(c.ProductName) : ''))
      .filter((n) => n.trim() !== '');
    return Array.from(new Set(names));
  }, [existingCategories]);

  // Item edit operations (Req 5.2-5.5) — each is a pure transition.
  const handleEditItem = (catIdx, itemIdx, field, value) =>
    setWorkingMenu((m) => applyEditItem(m, catIdx, itemIdx, field, value));
  const handleAddItem = (catIdx) => setWorkingMenu((m) => applyAddItem(m, catIdx));
  const handleDeleteItem = (catIdx, itemIdx) =>
    setWorkingMenu((m) => applyDeleteItem(m, catIdx, itemIdx));
  const handleMoveItem = (catIdx, itemIdx, direction) =>
    setWorkingMenu((m) => applyReorderItem(m, catIdx, itemIdx, direction));

  // Category operations (Req 5.6). These also keep the parallel mappings array
  // aligned by index with the working categories.
  const handleEditCategory = (catIdx, title) =>
    setWorkingMenu((m) => applyEditCategory(m, catIdx, title));

  // Set the meal period windows for an entire category (propagates to items
  // as a default; items may still override individually).
  const handleEditCategoryAvailableDuring = (catIdx, windows) =>
    setWorkingMenu((m) => applyEditCategoryAvailableDuring(m, catIdx, windows));

  // Set the meal period windows for a single item (overrides category when non-empty).
  const handleEditItemAvailableDuring = (catIdx, itemIdx, windows) =>
    setWorkingMenu((m) => applyEditItemAvailableDuring(m, catIdx, itemIdx, windows));
  const handleAddCategory = () => {
    setWorkingMenu((m) => applyAddCategory(m));
    setCategoryMappings((maps) => [...maps, makeDefaultCategoryMapping({ title: '' })]);
  };
  const handleDeleteCategory = (catIdx) => {
    setWorkingMenu((m) => applyDeleteCategory(m, catIdx));
    setCategoryMappings((maps) => maps.filter((_, ci) => ci !== catIdx));
  };

  // Category-mapping controls (Req 6.1): choose an existing category or
  // "create new" per extracted category. Intent is stored locally only.
  const handleMappingChange = (catIdx, selectValue) => {
    setCategoryMappings((maps) =>
      maps.map((mapping, ci) => {
        if (ci !== catIdx) return mapping;
        if (selectValue === '__create_new__') {
          const fallback = categories[catIdx] ? categories[catIdx].title : '';
          return {
            mode: MAP_MODE_NEW,
            existingName: '',
            newName: (mapping && mapping.newName) || fallback,
          };
        }
        return { mode: MAP_MODE_EXISTING, existingName: selectValue, newName: '' };
      })
    );
  };
  const handleNewCategoryNameChange = (catIdx, value) => {
    setCategoryMappings((maps) =>
      maps.map((mapping, ci) =>
        ci === catIdx ? { ...mapping, mode: MAP_MODE_NEW, newName: value } : mapping
      )
    );
  };

  // Explicit include/exclude decision for a (possibly flagged) item (Req 6.3).
  const setItemDecision = useCallback((targetName, itemTitle, included) => {
    setIncludeOverrides((prev) => ({
      ...prev,
      [itemDecisionKey(targetName, itemTitle)]: included,
    }));
  }, []);

  // Associate (or clear) an extracted image for an item (Req 10.2). Passing an
  // empty url clears the association so no imageUrl is attached (Req 11.1).
  const setItemImage = useCallback((targetName, itemTitle, imageUrl) => {
    setImageAssociations((prev) => {
      const key = itemDecisionKey(targetName, itemTitle);
      if (!imageUrl) {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: imageUrl };
    });
  }, []);

  // The image currently associated with an item, or '' when none (Req 11.1).
  const getItemImage = (targetName, itemTitle) => {
    const key = itemDecisionKey(targetName, itemTitle);
    return Object.prototype.hasOwnProperty.call(imageAssociations, key)
      ? imageAssociations[key]
      : '';
  };

  // --- AI-suggested modifier groups (Phase 3, Req 12–13) -------------------

  // The effective working entry for a proposed group: the Admin's edited/decided
  // entry when present, else a fresh 'pending' entry seeded from the normalized
  // proposal. Never mutates state (pure read).
  const getGroupEntry = (key, proposed) =>
    Object.prototype.hasOwnProperty.call(groupStates, key)
      ? groupStates[key]
      : { status: 'pending', group: normalizeProposedGroup(proposed) };

  // Apply a functional update to a group entry, seeding from the proposal when
  // the group has not been touched yet.
  const updateGroupEntry = (key, proposed, updater) => {
    setGroupStates((prev) => {
      const current = Object.prototype.hasOwnProperty.call(prev, key)
        ? prev[key]
        : { status: 'pending', group: normalizeProposedGroup(proposed) };
      return { ...prev, [key]: updater(current) };
    });
  };

  // Edit a group-level field (name, groupType, selection, min, max). Editing
  // does not by itself save anything (Req 12.4).
  const handleGroupFieldEdit = (key, proposed, field, value) =>
    updateGroupEntry(key, proposed, (cur) => ({
      ...cur,
      group: { ...cur.group, [field]: value },
    }));

  // Edit a single option field (name, priceDelta [dollars], defaultSelected).
  // A manually-entered price clears the "price not detected" flag.
  const handleOptionFieldEdit = (key, proposed, optIdx, field, value) =>
    updateGroupEntry(key, proposed, (cur) => ({
      ...cur,
      group: {
        ...cur.group,
        options: cur.group.options.map((o, i) =>
          i === optIdx
            ? { ...o, [field]: value, ...(field === 'priceDelta' ? { priceMissing: false } : {}) }
            : o
        ),
      },
    }));

  // Accept a group (Req 12.3): only allowed when validation passes (Req 13.1);
  // an invalid group is blocked and its error announced (Req 13.2). Accepting
  // clears any prior modifier error.
  const handleAcceptGroup = (key, proposed, label) => {
    const entry = getGroupEntry(key, proposed);
    const result = validateModifierGroup(entry.group);
    if (!result.ok) {
      setModifierError(`Can't accept "${label}": ${result.error}`);
      return;
    }
    setModifierError('');
    updateGroupEntry(key, proposed, (cur) => ({ ...cur, status: 'accepted' }));
  };

  // Discard a group (Req 12.3): it will not be included in the confirm payload.
  const handleDiscardGroup = (key, proposed) => {
    setModifierError('');
    updateGroupEntry(key, proposed, (cur) => ({ ...cur, status: 'discarded' }));
  };

  // Return an accepted, still-valid group back to "pending" so the Admin can
  // re-review it.
  const handleResetGroup = (key, proposed) => {
    setModifierError('');
    updateGroupEntry(key, proposed, (cur) => ({ ...cur, status: 'pending' }));
  };

  // Collect the storage-mapped Modifier_Groups for an item: ONLY groups the
  // Admin explicitly accepted AND that still pass validation (defense in depth,
  // so an edit that invalidated an accepted group can never be saved). Returns
  // [] when the item has no accepted groups (Req 12.4, 12.5, 13.1, 14.3).
  const collectAcceptedGroupsForItem = (targetName, item) => {
    const proposedGroups = readProposedGroups(item);
    const accepted = [];
    proposedGroups.forEach((proposed, groupIdx) => {
      const key = modifierGroupKey(targetName, item.title, groupIdx);
      const entry = getGroupEntry(key, proposed);
      if (entry.status !== 'accepted') return;
      if (!validateModifierGroup(entry.group).ok) return;
      accepted.push(mapGroupToStorage(entry.group));
    });
    return accepted;
  };

  // Effective inclusion for an item: an explicit Admin decision wins, else the
  // default (flagged duplicate => excluded — Req 6.4).
  const isItemIncluded = (targetName, item, duplicate) => {
    const key = itemDecisionKey(targetName, item.title);
    if (Object.prototype.hasOwnProperty.call(includeOverrides, key)) {
      return includeOverrides[key];
    }
    return defaultItemIncluded(duplicate);
  };

  // Build the effective include/exclude decision map for EVERY item, seeding
  // the duplicate default (excluded — Req 6.4) so the pure transform sees the
  // exact same inclusion the table shows. Keyed by content (itemDecisionKey)
  // so it matches transformMenuToBulkProducts' own lookup.
  const buildEffectiveDecisions = () => {
    const decisions = {};
    categories.forEach((category, catIdx) => {
      const mapping = categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
      const targetName = resolveTargetCategoryName(mapping, category);
      category.items.forEach((item) => {
        const duplicate = isDuplicateItem(item, targetName, existingProducts);
        decisions[itemDecisionKey(targetName, item.title)] = isItemIncluded(
          targetName,
          item,
          duplicate
        );
      });
    });
    return decisions;
  };

  // Walk the working menu in the SAME order transformMenuToBulkProducts emits
  // products (categories → included items) and record a locator for each
  // included item so a bulk-create result index maps back to its table row.
  const buildIncludedLocators = (decisions) => {
    const locators = [];
    let productIndex = 0;
    categories.forEach((category, catIdx) => {
      const mapping = categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
      const targetName = resolveTargetCategoryName(mapping, category);
      category.items.forEach((item, itemIdx) => {
        const key = itemDecisionKey(targetName, item.title);
        if (decisions[key]) {
          locators.push({ catIdx, itemIdx, productIndex });
          productIndex += 1;
        }
      });
    });
    return locators;
  };

  // Collect the "create new" target category names that actually receive at
  // least one included item, deduped against each other and existing catalog
  // categories (so we never POST a category that already exists).
  const collectNewCategoryNames = (decisions) => {
    const names = [];
    const seen = new Set(existingCategoryNames.map((n) => normalizeTitle(n)));
    categories.forEach((category, catIdx) => {
      const mapping = categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
      if (!mapping || mapping.mode !== MAP_MODE_NEW) return;
      const targetName = resolveTargetCategoryName(mapping, category);
      const norm = normalizeTitle(targetName);
      if (!norm || seen.has(norm)) return;
      const hasIncluded = category.items.some(
        (item) => decisions[itemDecisionKey(targetName, item.title)]
      );
      if (!hasIncluded) return;
      seen.add(norm);
      names.push(targetName);
    });
    return names;
  };

  // Create a single new category via the existing /admin/categories POST
  // convention (mirrors AdminCategories.js). Throws on a non-OK response so
  // the caller can record the failure in the summary.
  const createCategory = async (name, token) => {
    const response = await fetch(`${API_BASE_URL}/admin/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ProductName: name,
        Description: '',
        ImageURL: '',
        ParentCategoryID: null,
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error((data && data.error) || `Failed to create category "${name}"`);
    }
  };

  // Remove the successfully-created rows from the working menu, leaving failed
  // items (and any excluded items) in place so the Admin can correct and retry
  // them (Req 8.3).
  const removeCreatedItems = (createdLocators) => {
    if (createdLocators.length === 0) return;
    const byCategory = {};
    createdLocators.forEach(({ catIdx, itemIdx }) => {
      if (!byCategory[catIdx]) byCategory[catIdx] = new Set();
      byCategory[catIdx].add(itemIdx);
    });
    setWorkingMenu((m) => ({
      ...m,
      categories: m.categories.map((cat, ci) => {
        const removeSet = byCategory[ci];
        if (!removeSet) return cat;
        return { ...cat, items: cat.items.filter((_, ii) => !removeSet.has(ii)) };
      }),
    }));
  };

  // Confirm import (Req 7.1): transform → ensure new categories exist → bulk
  // create → summarize created/failed → retain failed rows. This is the ONLY
  // write path in the whole screen; it runs solely in response to the Admin
  // clicking Confirm.
  const handleConfirm = async () => {
    if (isConfirming) return;

    const decisions = buildEffectiveDecisions();
    const products = transformMenuToBulkProducts(workingMenu, categoryMappings, decisions);

    if (products.length === 0) {
      setSummary(null);
      setConfirmError(
        'There are no items selected to import. Include at least one item, then confirm.'
      );
      return;
    }

    setIsConfirming(true);
    setConfirmError('');
    setSummary(null);

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const locators = buildIncludedLocators(decisions);
      const newCategoryNames = collectNewCategoryNames(decisions);

      // Attach an associated image URL to a product body ONLY when the Admin
      // picked one for that item (Phase 2, Req 10.3); items with no association
      // are created with no image (Req 11.1). The transform (which intentionally
      // omits images) stays untouched — we post-process the products array here
      // by walking the same category→item order the transform used, matching
      // each product back to its row via the locator productIndex.
      locators.forEach(({ catIdx, itemIdx, productIndex }) => {
        const category = categories[catIdx];
        if (!category) return;
        const mapping = categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
        const targetName = resolveTargetCategoryName(mapping, category);
        const item = category.items[itemIdx];
        if (!item) return;
        const imageUrl = getItemImage(targetName, item.title);
        if (imageUrl && products[productIndex]) {
          products[productIndex] = { ...products[productIndex], imageUrl };
        }

        // Attach ONLY explicitly accepted, valid AI-suggested modifier groups
        // to this product body (Phase 3, Req 12.4, 12.5, 14.3). The pure
        // transform intentionally emits no ModifierGroups; we post-process here
        // (same step as the image association) so nothing unaccepted is ever
        // saved. Items with no accepted groups are left untouched.
        const acceptedGroups = collectAcceptedGroupsForItem(targetName, item);
        if (acceptedGroups.length > 0 && products[productIndex]) {
          products[productIndex] = {
            ...products[productIndex],
            ModifierGroups: acceptedGroups,
          };
        }
      });

      // 1. Ensure mapped "create new" categories exist before bulk create.
      let categoriesCreated = 0;
      const categoryErrors = [];
      for (const name of newCategoryNames) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await createCategory(name, token);
          categoriesCreated += 1;
        } catch (err) {
          categoryErrors.push({ name, error: err.message || 'Failed to create category' });
        }
      }

      // 2. Bulk create products through the existing /admin/products/bulk route.
      const results = await bulkCreateProducts(products, token);

      // 3. Account for every submitted item (no silent drops — Req 8.2).
      const createdLocators = [];
      const failures = [];
      let productsCreated = 0;
      results.forEach((r) => {
        const product = products[r.index] || {};
        if (r.ok) {
          productsCreated += 1;
          const loc = locators[r.index];
          if (loc) createdLocators.push(loc);
        } else {
          failures.push({
            title: product.ProductName || `Item ${r.index + 1}`,
            category: product.Category || '',
            error: r.error || 'Unknown error',
            statusCode: r.statusCode,
          });
        }
      });

      // 4. Retain failed rows; drop successfully-created ones (Req 8.3).
      removeCreatedItems(createdLocators);

      setSummary({
        categoriesCreated,
        categoryErrors,
        productsCreated,
        totalSubmitted: products.length,
        failures,
      });
    } catch (err) {
      setConfirmError('Something went wrong while importing. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <section aria-label="Review and edit extracted menu" style={styles.wrapper}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.heading}>
            {isManualEntry ? 'Build your menu' : 'Review extracted menu'}
          </h2>
          <p style={styles.subheading}>
            {isManualEntry
              ? 'Add categories and items, then confirm to create them.'
              : 'Edit anything that looks wrong before you confirm. Nothing is saved until you confirm.'}
          </p>
        </div>
        <div style={styles.summaryPill} aria-live="polite">
          {categories.length} {categories.length === 1 ? 'category' : 'categories'} ·{' '}
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </div>
      </div>

      {/* Announced error region for catalog-load failures (Req 15.3). */}
      <div role="alert" aria-live="assertive" style={styles.visuallyHidden}>
        {catalogError}
      </div>
      {catalogError && (
        <div style={styles.inlineWarning}>
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {catalogError}
        </div>
      )}

      {/* Flat_Scan informational note (Req 10.4, 11.2): this document had no
          separable embedded images to associate, so we import no photos and
          let the Admin know they can add photos manually later. Announced to
          assistive tech. */}
      {showFlatScanNote && (
        <div role="status" aria-live="polite" style={styles.inlineInfo}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> This looks like a
          scanned or photographed menu, so item photos couldn&apos;t be imported
          automatically. You can add photos to each product manually after import.
        </div>
      )}

      {categories.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={{ margin: 0 }}>No categories yet.</p>
          <button type="button" onClick={handleAddCategory} style={styles.btnPrimary}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add a category
          </button>
        </div>
      ) : (
        categories.map((category, catIdx) => (
          <fieldset key={catIdx} style={styles.categoryCard}>
            <legend style={styles.visuallyHidden}>
              Category {catIdx + 1}
            </legend>

            <div style={styles.categoryHeader}>
              <div style={{ flex: 1 }}>
                <label htmlFor={`cat-${catIdx}-title`} style={styles.fieldLabel}>
                  Category name
                </label>
                <input
                  id={`cat-${catIdx}-title`}
                  type="text"
                  value={category.title}
                  placeholder="Category name"
                  onChange={(e) => handleEditCategory(catIdx, e.target.value)}
                  style={styles.categoryTitleInput}
                />
              </div>
              <button
                type="button"
                onClick={() => handleDeleteCategory(catIdx)}
                style={styles.btnDanger}
                aria-label={`Delete category ${category.title || catIdx + 1}`}
              >
                <i className="fas fa-trash" aria-hidden="true"></i> Delete category
              </button>
            </div>

            {/* Category mapping (Req 6.1): map onto an existing catalog
                category or create a new one. Intent captured locally only. */}
            {(() => {
              const mapping = categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
              const isExisting = mapping.mode === MAP_MODE_EXISTING;
              const selectValue = isExisting ? mapping.existingName : '__create_new__';
              return (
                <div style={styles.mappingRow}>
                  <div style={{ minWidth: 260 }}>
                    <label htmlFor={`cat-${catIdx}-map`} style={styles.fieldLabel}>
                      Map to catalog category
                    </label>
                    <select
                      id={`cat-${catIdx}-map`}
                      value={selectValue}
                      onChange={(e) => handleMappingChange(catIdx, e.target.value)}
                      style={styles.select}
                    >
                      <option value="__create_new__">➕ Create new category</option>
                      {existingCategoryNames.length > 0 && (
                        <optgroup label="Existing categories">
                          {existingCategoryNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  {!isExisting && (
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label htmlFor={`cat-${catIdx}-newname`} style={styles.fieldLabel}>
                        New category name
                      </label>
                      <input
                        id={`cat-${catIdx}-newname`}
                        type="text"
                        value={mapping.newName}
                        placeholder="New category name"
                        onChange={(e) => handleNewCategoryNameChange(catIdx, e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Meal period assignment at category level. Only shown when
                serviceWindows are configured for this tenant. Checking a
                period here applies it as a default to all items in this
                category; items can still override individually below. */}
            {serviceWindowNames.length > 0 && (
              <div style={styles.mealPeriodRow}>
                <span style={styles.fieldLabel}>
                  Available during (applies to all items in this category)
                </span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {serviceWindowNames.map((name) => {
                    const current = Array.isArray(category.availableDuring) ? category.availableDuring : [];
                    const checked = current.some(w => String(w || '').toLowerCase() === name.toLowerCase());
                    return (
                      <label
                        key={name}
                        style={styles.periodChip}
                        title={`Set "${name}" as meal period for all items in this category`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? current.filter(w => String(w || '').toLowerCase() !== name.toLowerCase())
                              : [...current, name];
                            handleEditCategoryAvailableDuring(catIdx, next);
                          }}
                          style={{ marginRight: 4 }}
                        />
                        {name}
                      </label>
                    );
                  })}
                </div>
                <p style={styles.fieldHint}>
                  Leave blank for all-day. Item-level selection overrides this.
                </p>
              </div>
            )}

            <table style={styles.table}>
              <thead>
                <tr>
                  <th scope="col" style={styles.th}>Item</th>
                  <th scope="col" style={styles.th}>Description</th>
                  <th scope="col" style={{ ...styles.th, width: 120 }}>Price</th>
                  {serviceWindowNames.length > 0 && (
                    <th scope="col" style={{ ...styles.th, width: 170 }}>Meal Period</th>
                  )}
                  {hasEmbeddedImages && (
                    <th scope="col" style={{ ...styles.th, width: 180 }}>Photo</th>
                  )}
                  <th scope="col" style={{ ...styles.th, width: 200 }}>Import</th>
                  <th scope="col" style={{ ...styles.th, width: 190 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {category.items.length === 0 ? (
                  <tr>
                    <td colSpan={itemColumnCount} style={styles.emptyRow}>
                      No items in this category yet.
                    </td>
                  </tr>
                ) : (
                  category.items.map((item, itemIdx) => {
                    const mapping =
                      categoryMappings[catIdx] || makeDefaultCategoryMapping(category);
                    const targetName = resolveTargetCategoryName(mapping, category);
                    const duplicate = isDuplicateItem(item, targetName, existingProducts);
                    const included = isItemIncluded(targetName, item, duplicate);
                    // AI-suggested modifier groups for this item (Phase 3, Req
                    // 12.2). Absent in Phase 1/2 → no sub-row is rendered.
                    const proposedGroups = readProposedGroups(item);
                    return (
                    <React.Fragment key={itemIdx}>
                    <tr style={duplicate && !included ? styles.excludedRow : undefined}>
                      <td style={styles.td}>
                        <label
                          htmlFor={`item-${catIdx}-${itemIdx}-title`}
                          style={styles.visuallyHidden}
                        >
                          Item title for row {itemIdx + 1} in category {catIdx + 1}
                        </label>
                        <input
                          id={`item-${catIdx}-${itemIdx}-title`}
                          type="text"
                          value={item.title}
                          placeholder="Item name"
                          onChange={(e) =>
                            handleEditItem(catIdx, itemIdx, 'title', e.target.value)
                          }
                          style={styles.input}
                        />
                      </td>
                      <td style={styles.td}>
                        <label
                          htmlFor={`item-${catIdx}-${itemIdx}-description`}
                          style={styles.visuallyHidden}
                        >
                          Item description for row {itemIdx + 1} in category {catIdx + 1}
                        </label>
                        <textarea
                          id={`item-${catIdx}-${itemIdx}-description`}
                          value={item.description}
                          placeholder="Description (includes any options/add-ons)"
                          rows={2}
                          onChange={(e) =>
                            handleEditItem(catIdx, itemIdx, 'description', e.target.value)
                          }
                          style={styles.textarea}
                        />
                      </td>
                      <td style={styles.td}>
                        <label
                          htmlFor={`item-${catIdx}-${itemIdx}-price`}
                          style={styles.visuallyHidden}
                        >
                          Item price for row {itemIdx + 1} in category {catIdx + 1}
                        </label>
                        <input
                          id={`item-${catIdx}-${itemIdx}-price`}
                          type="text"
                          value={item.price}
                          placeholder="0.00"
                          inputMode="decimal"
                          onChange={(e) =>
                            handleEditItem(catIdx, itemIdx, 'price', e.target.value)
                          }
                          style={styles.input}
                        />
                      </td>
                      {/* Optional meal period chips per item. Only shown when
                          serviceWindows are configured. Empty = inherit from
                          category. Non-empty = overrides category selection. */}
                      {serviceWindowNames.length > 0 && (
                        <td style={{ ...styles.td, minWidth: 160 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {serviceWindowNames.map((name) => {
                              const itemWindows = Array.isArray(item.availableDuring) ? item.availableDuring : [];
                              const catWindows = Array.isArray(category.availableDuring) ? category.availableDuring : [];
                              // Show inherited (from category) as indeterminate-style
                              const itemHas = itemWindows.some(w => String(w || '').toLowerCase() === name.toLowerCase());
                              const catHas = catWindows.some(w => String(w || '').toLowerCase() === name.toLowerCase());
                              // Effective = item overrides if item has any selection, else use cat
                              const effective = itemWindows.length > 0 ? itemHas : catHas;
                              const inherited = itemWindows.length === 0 && catHas;
                              return (
                                <label
                                  key={name}
                                  style={{ ...styles.periodChipSmall, opacity: inherited ? 0.6 : 1 }}
                                  title={inherited ? `Inherited from category: "${name}"` : name}
                                >
                                  <input
                                    type="checkbox"
                                    checked={effective}
                                    onChange={() => {
                                      // First click activates item-level control
                                      const base = itemWindows.length > 0 ? itemWindows : [...catWindows];
                                      const next = effective
                                        ? base.filter(w => String(w || '').toLowerCase() !== name.toLowerCase())
                                        : [...base, name];
                                      handleEditItemAvailableDuring(catIdx, itemIdx, next);
                                    }}
                                    style={{ marginRight: 3 }}
                                  />
                                  {name}{inherited ? ' (cat)' : ''}
                                </label>
                              );
                            })}
                            {Array.isArray(item.availableDuring) && item.availableDuring.length > 0 && (
                              <button
                                type="button"
                                onClick={() => handleEditItemAvailableDuring(catIdx, itemIdx, [])}
                                style={styles.btnClearPeriod}
                                title="Clear item-level override — inherit from category"
                              >
                                ↩ use category
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                      {/* Optional image association (Phase 2, Req 10.2). Only
                          offered when the document carried embedded images. */}
                      {hasEmbeddedImages && (() => {
                        const associated = getItemImage(targetName, item.title);
                        const selectId = `item-${catIdx}-${itemIdx}-image`;
                        return (
                          <td style={styles.td}>
                            <label htmlFor={selectId} style={styles.visuallyHidden}>
                              Photo for row {itemIdx + 1} in category {catIdx + 1}
                            </label>
                            <select
                              id={selectId}
                              value={associated}
                              onChange={(e) =>
                                setItemImage(targetName, item.title, e.target.value)
                              }
                              style={styles.select}
                            >
                              <option value="">No photo</option>
                              {availableImages.map((img) => (
                                <option key={img.url} value={img.url}>
                                  {img.label}
                                </option>
                              ))}
                            </select>
                            {associated && (
                              <img
                                src={associated}
                                alt={`Associated photo for ${item.title || `item ${itemIdx + 1}`}`}
                                style={styles.thumb}
                              />
                            )}
                          </td>
                        );
                      })()}
                      <td style={styles.td}>
                        {duplicate ? (
                          <div>
                            <span style={styles.dupBadge} title="Matches an existing product in this category">
                              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>{' '}
                              Possible duplicate
                            </span>
                            <div
                              role="radiogroup"
                              aria-label={`Import decision for ${item.title || `item ${itemIdx + 1}`}`}
                              style={styles.dupChoices}
                            >
                              <label style={styles.dupChoiceLabel}>
                                <input
                                  type="radio"
                                  name={`dup-${catIdx}-${itemIdx}`}
                                  checked={!included}
                                  onChange={() =>
                                    setItemDecision(targetName, item.title, false)
                                  }
                                />{' '}
                                Exclude
                              </label>
                              <label style={styles.dupChoiceLabel}>
                                <input
                                  type="radio"
                                  name={`dup-${catIdx}-${itemIdx}`}
                                  checked={included}
                                  onChange={() =>
                                    setItemDecision(targetName, item.title, true)
                                  }
                                />{' '}
                                Create anyway
                              </label>
                            </div>
                          </div>
                        ) : (
                          <span style={styles.includedText}>
                            <i className="fas fa-check" aria-hidden="true"></i> Will import
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.rowActions}>
                          <button
                            type="button"
                            onClick={() => handleMoveItem(catIdx, itemIdx, 'up')}
                            disabled={itemIdx === 0}
                            style={styles.btnIcon}
                            aria-label={`Move ${item.title || `item ${itemIdx + 1}`} up`}
                            title="Move up"
                          >
                            <i className="fas fa-arrow-up" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveItem(catIdx, itemIdx, 'down')}
                            disabled={itemIdx === category.items.length - 1}
                            style={styles.btnIcon}
                            aria-label={`Move ${item.title || `item ${itemIdx + 1}`} down`}
                            title="Move down"
                          >
                            <i className="fas fa-arrow-down" aria-hidden="true"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(catIdx, itemIdx)}
                            style={styles.btnIcon}
                            aria-label={`Delete ${item.title || `item ${itemIdx + 1}`}`}
                            title="Delete item"
                          >
                            <i className="fas fa-trash" aria-hidden="true"></i>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* AI-suggested modifier groups (Phase 3, Req 12.2, 12.3,
                        13.2). Each proposed group is clearly labeled
                        "AI-suggested — review required", is editable, and must
                        be explicitly accepted (and pass min/max validation) to
                        be included on confirm. Nothing is auto-saved. */}
                    {proposedGroups.length > 0 && (
                      <tr>
                        <td colSpan={itemColumnCount} style={styles.modifierCell}>
                          <div style={styles.modifierWrap}>
                            {proposedGroups.map((proposed, groupIdx) => {
                              const gKey = modifierGroupKey(targetName, item.title, groupIdx);
                              const entry = getGroupEntry(gKey, proposed);
                              const g = entry.group;
                              const validation = validateModifierGroup(g);
                              const isAccepted = entry.status === 'accepted';
                              const isDiscarded = entry.status === 'discarded';
                              const groupLabel =
                                g.name || `Option group ${groupIdx + 1}`;
                              const idBase = `mod-${catIdx}-${itemIdx}-${groupIdx}`;
                              const isTemplate = asString(g.optionsSource).toUpperCase() === 'TEMPLATE';
                              return (
                                <fieldset
                                  key={gKey}
                                  style={{
                                    ...styles.modifierCard,
                                    ...(isDiscarded ? styles.modifierCardDiscarded : {}),
                                    ...(isAccepted ? styles.modifierCardAccepted : {}),
                                  }}
                                >
                                  <legend style={styles.visuallyHidden}>
                                    AI-suggested option group {groupIdx + 1} for{' '}
                                    {item.title || `item ${itemIdx + 1}`}
                                  </legend>

                                  <div style={styles.modifierHeader}>
                                    <span style={styles.aiBadge}>
                                      <i className="fas fa-wand-magic-sparkles" aria-hidden="true"></i>{' '}
                                      AI-suggested — review required
                                    </span>
                                    {isAccepted && (
                                      <span style={styles.statusAccepted}>
                                        <i className="fas fa-check" aria-hidden="true"></i> Accepted
                                      </span>
                                    )}
                                    {isDiscarded && (
                                      <span style={styles.statusDiscarded}>
                                        <i className="fas fa-ban" aria-hidden="true"></i> Discarded
                                      </span>
                                    )}
                                  </div>

                                  <div style={styles.modifierFieldRow}>
                                    <div style={styles.modifierField}>
                                      <label htmlFor={`${idBase}-name`} style={styles.fieldLabel}>
                                        Group name
                                      </label>
                                      <input
                                        id={`${idBase}-name`}
                                        type="text"
                                        value={g.name}
                                        placeholder="e.g. Size"
                                        onChange={(e) =>
                                          handleGroupFieldEdit(gKey, proposed, 'name', e.target.value)
                                        }
                                        style={styles.input}
                                      />
                                    </div>
                                    <div style={styles.modifierFieldNarrow}>
                                      <label htmlFor={`${idBase}-type`} style={styles.fieldLabel}>
                                        Type
                                      </label>
                                      <select
                                        id={`${idBase}-type`}
                                        value={normalizeGroupType(g.groupType)}
                                        onChange={(e) =>
                                          handleGroupFieldEdit(gKey, proposed, 'groupType', e.target.value)
                                        }
                                        style={styles.select}
                                      >
                                        {MODIFIER_GROUP_TYPES.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div style={styles.modifierFieldNarrow}>
                                      <label htmlFor={`${idBase}-selection`} style={styles.fieldLabel}>
                                        Selection
                                      </label>
                                      <select
                                        id={`${idBase}-selection`}
                                        value={normalizeSelection(g.selection)}
                                        onChange={(e) =>
                                          handleGroupFieldEdit(gKey, proposed, 'selection', e.target.value)
                                        }
                                        style={styles.select}
                                      >
                                        <option value="Single">Single</option>
                                        <option value="Multiple">Multiple</option>
                                      </select>
                                    </div>
                                    <div style={styles.modifierFieldTiny}>
                                      <label htmlFor={`${idBase}-min`} style={styles.fieldLabel}>
                                        Min
                                      </label>
                                      <input
                                        id={`${idBase}-min`}
                                        type="number"
                                        min="0"
                                        value={g.min}
                                        onChange={(e) =>
                                          handleGroupFieldEdit(gKey, proposed, 'min', e.target.value)
                                        }
                                        style={styles.input}
                                      />
                                    </div>
                                    <div style={styles.modifierFieldTiny}>
                                      <label htmlFor={`${idBase}-max`} style={styles.fieldLabel}>
                                        Max
                                      </label>
                                      <input
                                        id={`${idBase}-max`}
                                        type="number"
                                        min="0"
                                        value={g.max}
                                        onChange={(e) =>
                                          handleGroupFieldEdit(gKey, proposed, 'max', e.target.value)
                                        }
                                        style={styles.input}
                                      />
                                    </div>
                                  </div>

                                  {isTemplate ? (
                                    <p style={styles.templateNote}>
                                      <i className="fas fa-link" aria-hidden="true"></i> Uses a
                                      reusable option template
                                      {g.templateId ? ` (${g.templateId})` : ''}.
                                    </p>
                                  ) : (
                                    <div style={styles.optionsBlock}>
                                      {g.options.length === 0 ? (
                                        <p style={styles.optionsEmpty}>No options suggested.</p>
                                      ) : (
                                        g.options.map((opt, optIdx) => (
                                          <div key={optIdx} style={styles.optionRow}>
                                            <div style={styles.optionNameField}>
                                              <label
                                                htmlFor={`${idBase}-opt-${optIdx}-name`}
                                                style={styles.visuallyHidden}
                                              >
                                                Option {optIdx + 1} name for {groupLabel}
                                              </label>
                                              <input
                                                id={`${idBase}-opt-${optIdx}-name`}
                                                type="text"
                                                value={opt.name}
                                                placeholder="Option name"
                                                onChange={(e) =>
                                                  handleOptionFieldEdit(
                                                    gKey,
                                                    proposed,
                                                    optIdx,
                                                    'name',
                                                    e.target.value
                                                  )
                                                }
                                                style={styles.input}
                                              />
                                            </div>
                                            <div style={styles.optionPriceField}>
                                              <label
                                                htmlFor={`${idBase}-opt-${optIdx}-price`}
                                                style={styles.visuallyHidden}
                                              >
                                                Option {optIdx + 1} extra charge in dollars for {groupLabel}
                                              </label>
                                              <input
                                                id={`${idBase}-opt-${optIdx}-price`}
                                                type="text"
                                                inputMode="decimal"
                                                value={opt.priceDelta}
                                                placeholder="0.00"
                                                onChange={(e) =>
                                                  handleOptionFieldEdit(
                                                    gKey,
                                                    proposed,
                                                    optIdx,
                                                    'priceDelta',
                                                    e.target.value
                                                  )
                                                }
                                                style={styles.input}
                                              />
                                              <span style={styles.priceUnit}>$ extra</span>
                                            </div>
                                            <label
                                              htmlFor={`${idBase}-opt-${optIdx}-default`}
                                              style={styles.optionDefaultLabel}
                                            >
                                              <input
                                                id={`${idBase}-opt-${optIdx}-default`}
                                                type="checkbox"
                                                checked={Boolean(opt.defaultSelected)}
                                                onChange={(e) =>
                                                  handleOptionFieldEdit(
                                                    gKey,
                                                    proposed,
                                                    optIdx,
                                                    'defaultSelected',
                                                    e.target.checked
                                                  )
                                                }
                                              />{' '}
                                              Default
                                            </label>
                                            {opt.priceMissing && (
                                              <span style={styles.priceMissingNote} title="No price detected">
                                                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>{' '}
                                                No price detected — set to 0, please confirm
                                              </span>
                                            )}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}

                                  {!validation.ok && (
                                    <p style={styles.modifierInlineError}>
                                      <i className="fas fa-exclamation-circle" aria-hidden="true"></i>{' '}
                                      {validation.error}
                                    </p>
                                  )}

                                  <div style={styles.modifierActions}>
                                    {isAccepted ? (
                                      <button
                                        type="button"
                                        onClick={() => handleResetGroup(gKey, proposed)}
                                        style={styles.btnSecondary}
                                      >
                                        <i className="fas fa-rotate-left" aria-hidden="true"></i> Undo accept
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleAcceptGroup(gKey, proposed, groupLabel)}
                                        disabled={!validation.ok}
                                        style={{
                                          ...styles.btnPrimary,
                                          ...(validation.ok ? {} : { opacity: 0.6, cursor: 'not-allowed' }),
                                        }}
                                        aria-label={`Accept option group ${groupLabel}`}
                                      >
                                        <i className="fas fa-check" aria-hidden="true"></i> Accept
                                      </button>
                                    )}
                                    {isDiscarded ? (
                                      <button
                                        type="button"
                                        onClick={() => handleResetGroup(gKey, proposed)}
                                        style={styles.btnSecondary}
                                      >
                                        <i className="fas fa-rotate-left" aria-hidden="true"></i> Restore
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleDiscardGroup(gKey, proposed)}
                                        style={styles.btnDanger}
                                        aria-label={`Discard option group ${groupLabel}`}
                                      >
                                        <i className="fas fa-ban" aria-hidden="true"></i> Discard
                                      </button>
                                    )}
                                  </div>
                                </fieldset>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>

            <button
              type="button"
              onClick={() => handleAddItem(catIdx)}
              style={styles.btnSecondary}
              aria-label={`Add item to category ${category.title || catIdx + 1}`}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add item
            </button>
          </fieldset>
        ))
      )}

      {/* Announced modifier-validation region (Req 13.2, 15.3): a blocked
          accept on an invalid group surfaces its reason here for assistive
          tech. */}
      <div role="alert" aria-live="assertive" style={styles.visuallyHidden}>
        {modifierError}
      </div>
      {modifierError && (
        <div style={styles.inlineError} role="presentation">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {modifierError}
        </div>
      )}

      {/* Announced confirm-error region (Req 15.3). */}
      <div role="alert" aria-live="assertive" style={styles.visuallyHidden}>
        {confirmError}
      </div>
      {confirmError && (
        <div style={styles.inlineError} role="presentation">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {confirmError}
        </div>
      )}

      {/* Created/failed summary after a confirm (Req 7.3, 8.2). Rendered in an
          announced region so assistive tech reports the outcome (Req 15.3). */}
      {summary && (
        <div role="status" aria-live="polite" style={styles.summaryBox}>
          <p style={styles.summaryHeadline}>
            <i className="fas fa-check-circle" aria-hidden="true"></i>{' '}
            Created {summary.productsCreated} of {summary.totalSubmitted}{' '}
            {summary.totalSubmitted === 1 ? 'product' : 'products'}
            {summary.categoriesCreated > 0 && (
              <>
                {' '}and {summary.categoriesCreated}{' '}
                {summary.categoriesCreated === 1 ? 'category' : 'categories'}
              </>
            )}
            .
          </p>

          {summary.categoryErrors.length > 0 && (
            <div style={styles.summaryFailBlock}>
              <p style={styles.summaryFailTitle}>
                {summary.categoryErrors.length}{' '}
                {summary.categoryErrors.length === 1 ? 'category' : 'categories'} could not
                be created:
              </p>
              <ul style={styles.summaryList}>
                {summary.categoryErrors.map((ce, i) => (
                  <li key={i}>
                    <strong>{ce.name || '(unnamed)'}</strong> — {ce.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {summary.failures.length > 0 ? (
            <div style={styles.summaryFailBlock}>
              <p style={styles.summaryFailTitle}>
                {summary.failures.length}{' '}
                {summary.failures.length === 1 ? 'item' : 'items'} could not be created and{' '}
                {summary.failures.length === 1 ? 'remains' : 'remain'} in the table below for
                you to fix and retry:
              </p>
              <ul style={styles.summaryList}>
                {summary.failures.map((f, i) => (
                  <li key={i}>
                    <strong>{f.title}</strong>
                    {f.category ? ` (${f.category})` : ''} —{' '}
                    {f.error}
                    {typeof f.statusCode === 'number' ? ` [${f.statusCode}]` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p style={styles.summaryAllGood}>
              All selected items were created successfully.
            </p>
          )}
        </div>
      )}

      <div style={styles.footer}>
        {categories.length > 0 && (
          <button type="button" onClick={handleAddCategory} style={styles.btnSecondary}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add category
          </button>
        )}
        <div style={{ flex: 1 }} />
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            style={styles.btnSecondary}
            disabled={isConfirming}
          >
            Cancel
          </button>
        )}
        {/*
          Confirm import (task 9.5): the SOLE catalog write path. Transforms the
          reviewed, non-excluded menu, ensures mapped "create new" categories
          exist, then bulk-creates via POST /admin/products/bulk (Req 7.1).
        */}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirming || totalItems === 0}
          aria-busy={isConfirming}
          style={{
            ...styles.btnPrimary,
            ...(isConfirming || totalItems === 0
              ? { opacity: 0.6, cursor: 'not-allowed' }
              : {}),
          }}
        >
          {isConfirming ? (
            <>
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Importing…
            </>
          ) : (
            <>
              <i className="fas fa-check" aria-hidden="true"></i> Confirm import
            </>
          )}
        </button>
      </div>
    </section>
  );
}

const styles = {
  wrapper: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid rgba(148, 163, 184, 0.25)',
    padding: 24,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  heading: { margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' },
  subheading: { margin: '4px 0 0', color: 'rgba(15, 23, 42, 0.7)' },
  summaryPill: {
    padding: '6px 12px',
    background: 'rgba(76, 29, 149, 0.08)',
    color: '#4c1d95',
    borderRadius: 999,
    fontWeight: 600,
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
  categoryCard: {
    border: '1px solid rgba(148, 163, 184, 0.35)',
    borderRadius: 10,
    padding: 16,
    margin: '0 0 16px',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 12,
  },
  fieldLabel: {
    display: 'block',
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: 4,
    color: '#1e293b',
  },
  categoryTitleInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 8,
    fontSize: '1rem',
    fontWeight: 600,
    boxSizing: 'border-box',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 12 },
  th: {
    textAlign: 'left',
    padding: '8px',
    borderBottom: '2px solid rgba(148, 163, 184, 0.3)',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'rgba(15, 23, 42, 0.6)',
  },
  td: {
    padding: '8px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    verticalAlign: 'top',
  },
  emptyRow: {
    padding: '12px 8px',
    color: 'rgba(15, 23, 42, 0.5)',
    fontStyle: 'italic',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 8,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 8,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  rowActions: { display: 'flex', gap: 6 },
  mappingRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    marginBottom: 12,
    padding: '10px 12px',
    background: 'rgba(76, 29, 149, 0.04)',
    borderRadius: 8,
  },
  mealPeriodRow: {
    marginBottom: 12,
    padding: '10px 12px',
    background: 'rgba(16, 185, 129, 0.05)',
    borderRadius: 8,
    border: '1px solid rgba(16, 185, 129, 0.2)',
  },
  periodChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    background: '#fff',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 20,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  periodChipSmall: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.8rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnClearPeriod: {
    background: 'none',
    border: 'none',
    color: 'rgba(76, 29, 149, 0.7)',
    fontSize: '0.75rem',
    cursor: 'pointer',
    padding: '2px 0',
    textDecoration: 'underline',
  },
  fieldHint: {
    margin: '4px 0 0',
    fontSize: '0.8rem',
    color: 'rgba(15, 23, 42, 0.5)',
  },
  select: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 8,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    background: '#fff',
  },
  inlineWarning: {
    padding: '8px 12px',
    background: 'rgba(202, 138, 4, 0.1)',
    color: '#854d0e',
    borderRadius: 8,
    fontSize: '0.9rem',
    marginBottom: 12,
  },
  inlineInfo: {
    padding: '8px 12px',
    background: 'rgba(37, 99, 235, 0.08)',
    color: '#1e40af',
    borderRadius: 8,
    fontSize: '0.9rem',
    marginBottom: 12,
  },
  thumb: {
    marginTop: 6,
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 6,
    border: '1px solid rgba(148, 163, 184, 0.5)',
    display: 'block',
  },
  inlineError: {
    padding: '8px 12px',
    background: 'rgba(185, 28, 28, 0.1)',
    color: '#b91c1c',
    borderRadius: 8,
    fontSize: '0.9rem',
    marginBottom: 12,
  },
  summaryBox: {
    padding: '14px 16px',
    background: 'rgba(21, 128, 61, 0.06)',
    border: '1px solid rgba(21, 128, 61, 0.25)',
    borderRadius: 10,
    marginBottom: 16,
  },
  summaryHeadline: {
    margin: '0 0 8px',
    fontWeight: 700,
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  summaryFailBlock: {
    marginTop: 8,
    padding: '10px 12px',
    background: 'rgba(185, 28, 28, 0.08)',
    borderRadius: 8,
  },
  summaryFailTitle: {
    margin: '0 0 6px',
    fontWeight: 600,
    color: '#b91c1c',
  },
  summaryList: {
    margin: 0,
    paddingLeft: 20,
    color: '#1e293b',
    fontSize: '0.9rem',
  },
  summaryAllGood: {
    margin: '4px 0 0',
    color: '#166534',
    fontSize: '0.9rem',
  },
  excludedRow: { opacity: 0.55 },
  dupBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px',
    background: 'rgba(202, 138, 4, 0.12)',
    color: '#854d0e',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  dupChoices: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 },
  dupChoiceLabel: {
    fontSize: '0.85rem',
    color: '#1e293b',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  includedText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#15803d',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
    padding: '24px 0',
    color: 'rgba(15, 23, 42, 0.7)',
  },
  btnPrimary: {
    padding: '10px 18px',
    background: '#4c1d95',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  btnSecondary: {
    padding: '10px 18px',
    background: '#fff',
    color: '#4c1d95',
    border: '1px solid rgba(76, 29, 149, 0.35)',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  btnDanger: {
    padding: '8px 14px',
    background: '#fff',
    color: '#b91c1c',
    border: '1px solid rgba(185, 28, 28, 0.35)',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },
  btnIcon: {
    padding: '6px 10px',
    background: '#fff',
    color: '#475569',
    border: '1px solid rgba(148, 163, 184, 0.5)',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // AI-suggested modifier groups (Phase 3).
  modifierCell: {
    padding: '4px 8px 12px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    background: 'rgba(76, 29, 149, 0.03)',
  },
  modifierWrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  modifierCard: {
    border: '1px dashed rgba(76, 29, 149, 0.45)',
    borderRadius: 8,
    padding: 12,
    margin: 0,
    background: '#fff',
  },
  modifierCardAccepted: {
    borderStyle: 'solid',
    borderColor: 'rgba(21, 128, 61, 0.5)',
    background: 'rgba(21, 128, 61, 0.04)',
  },
  modifierCardDiscarded: { opacity: 0.6 },
  modifierHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  aiBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    background: 'rgba(76, 29, 149, 0.12)',
    color: '#4c1d95',
    borderRadius: 999,
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  statusAccepted: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: '#166534',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  statusDiscarded: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: '#b91c1c',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  modifierFieldRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    marginBottom: 8,
  },
  modifierField: { flex: 1, minWidth: 160 },
  modifierFieldNarrow: { width: 150 },
  modifierFieldTiny: { width: 72 },
  templateNote: {
    margin: '4px 0 0',
    color: 'rgba(15, 23, 42, 0.7)',
    fontSize: '0.85rem',
  },
  optionsBlock: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 },
  optionsEmpty: {
    margin: 0,
    color: 'rgba(15, 23, 42, 0.5)',
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
  optionRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  optionNameField: { flex: 1, minWidth: 160 },
  optionPriceField: { display: 'flex', alignItems: 'center', gap: 4, width: 130 },
  priceUnit: { fontSize: '0.78rem', color: 'rgba(15, 23, 42, 0.6)', whiteSpace: 'nowrap' },
  optionDefaultLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.85rem',
    color: '#1e293b',
    cursor: 'pointer',
  },
  priceMissingNote: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: '#854d0e',
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  modifierInlineError: {
    margin: '8px 0 0',
    color: '#b91c1c',
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  modifierActions: {
    display: 'flex',
    gap: 8,
    marginTop: 10,
    flexWrap: 'wrap',
  },
};
